/**
 * WorksheetHistoryTimeline — page11 (worksheet/02-detail-6tab) Előzmény-tab
 * vertikális esemény-timeline (v3-mockup parity).
 *
 * A timeline DERIVED: a read-DTO lifecycle-timestampjeiből épül. A
 * `WorksheetLifecycleTimestamps` mezők a 2026-06-11-i éjszakai contract-append
 * után érkeznek a read-response-ban — addig opcionálisak, fabrikált esemény
 * nincs. Per-esemény user-attribúció a WorksheetStatusHistory táblára vár
 * (Javo-döntés) — szándékosan nem jelenítünk meg kitalált usert.
 */

import type { Worksheet, WorksheetStatus } from '@/api/worksheets';
import { useTranslation } from '@kgc/i18n';
import type { WorksheetStatusHistoryEntryDto } from '@kgc/service-worksheet/worksheet-dto';
import { STATUS_LABEL_KEYS } from '../types';

export interface WorksheetHistoryEvent {
  /** ISO timestamp. */
  at: string;
  title: string;
  description?: string;
  tone?: 'success' | 'info' | 'muted';
}

/** A contract-append (éjszakai seam) után érkező opcionális lifecycle-mezők. */
export interface WorksheetLifecycleTimestamps {
  diagnosedAt?: string;
  startedAt?: string;
  deliveredAt?: string;
  customerNotifiedAt?: string;
  closedAt?: string;
}

type TranslateFn = (key: string) => string;

/**
 * Derivált esemény-lista a worksheet elérhető timestampjeiből,
 * időrend szerint csökkenő sorrendben (legfrissebb elöl).
 */
export function buildWorksheetHistoryEvents(
  worksheet: Worksheet & WorksheetLifecycleTimestamps,
  t: TranslateFn
): WorksheetHistoryEvent[] {
  const candidates: Array<{
    at: string | undefined;
    key: string;
    tone: WorksheetHistoryEvent['tone'];
  }> = [
    { at: worksheet.receivedAt, key: 'worksheet.historyEvents.received', tone: 'info' },
    { at: worksheet.createdAt, key: 'worksheet.historyEvents.created', tone: 'muted' },
    { at: worksheet.diagnosedAt, key: 'worksheet.historyEvents.diagnosed', tone: 'info' },
    { at: worksheet.startedAt, key: 'worksheet.historyEvents.started', tone: 'info' },
    { at: worksheet.completedAt, key: 'worksheet.historyEvents.completed', tone: 'success' },
    { at: worksheet.customerNotifiedAt, key: 'worksheet.historyEvents.notified', tone: 'info' },
    { at: worksheet.deliveredAt, key: 'worksheet.historyEvents.delivered', tone: 'success' },
    { at: worksheet.closedAt, key: 'worksheet.historyEvents.closed', tone: 'success' },
  ];

  return candidates
    .filter((c): c is { at: string; key: string; tone: WorksheetHistoryEvent['tone'] } =>
      Boolean(c.at)
    )
    .map(c => ({ at: c.at, title: t(c.key), tone: c.tone }))
    .sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
}

// ── Státusz-történet → események (#433/#438, user-attribúciós réteg) ────────

const STATUS_HISTORY_TONE: Record<string, NonNullable<WorksheetHistoryEvent['tone']>> = {
  KESZ: 'success',
  LEZART: 'success',
  TOROLVE: 'muted',
  FELFUGGESZTVE: 'muted',
};

function statusHistoryLabel(status: string, t: TranslateFn): string {
  const key = STATUS_LABEL_KEYS[status as WorksheetStatus] as string | undefined;
  return key ? t(key) : status;
}

/** NaN-safe rendezőkulcs: invalid changedAt a desc-lista végére (4-lens finding). */
function changedAtTime(iso: string): number {
  const ms = new Date(iso).getTime();
  return Number.isNaN(ms) ? Number.NEGATIVE_INFINITY : ms;
}

/**
 * User-attribúciós esemény-lista a státusz-történetből (#433 endpoint,
 * #438 changedByUserName). Nem-üres history esetén EZ a timeline forrása;
 * üres history → a derived buildWorksheetHistoryEvents a fallback.
 */
export function buildStatusHistoryEvents(
  entries: WorksheetStatusHistoryEntryDto[],
  t: TranslateFn
): WorksheetHistoryEvent[] {
  return [...entries]
    .sort((a, b) => changedAtTime(b.changedAt) - changedAtTime(a.changedAt))
    .map(entry => {
      const toLabel = statusHistoryLabel(entry.toStatus, t);
      const title = entry.fromStatus
        ? `${statusHistoryLabel(entry.fromStatus, t)} → ${toLabel}`
        : toLabel;
      // #438 seam-szemantika: törölt/ismeretlen user → name kimarad a wire-ről,
      // a changedByUserId marad → rövidített-ID fallback (Zeno-ratify).
      const userLabel =
        entry.changedByUserName ??
        (entry.changedByUserId ? `${entry.changedByUserId.slice(0, 8)}…` : undefined);
      const description = [userLabel, entry.note].filter(Boolean).join(' — ') || undefined;
      return {
        at: entry.changedAt,
        title,
        ...(description !== undefined ? { description } : {}),
        tone: STATUS_HISTORY_TONE[entry.toStatus] ?? 'info',
      };
    });
}

const DOT_TONE_CLASS: Record<NonNullable<WorksheetHistoryEvent['tone']> | 'default', string> = {
  success: 'bg-emerald-500',
  info: 'bg-sky-500',
  muted: 'bg-gray-400',
  default: 'bg-sky-500',
};

function formatEventTime(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString('hu-HU', { dateStyle: 'short', timeStyle: 'short' });
}

export function WorksheetHistoryTimeline({ events }: { events: WorksheetHistoryEvent[] }) {
  const { t } = useTranslation();

  if (events.length === 0) {
    return <p className="text-sm text-muted-foreground">{t('worksheet.historyEvents.empty')}</p>;
  }

  return (
    <ol
      data-testid="worksheet-history-timeline"
      className="relative ml-2 space-y-4 border-l-2 border-gray-200 dark:border-gray-700"
    >
      {events.map(event => (
        <li
          key={`${event.at}-${event.title}`}
          data-testid="worksheet-history-event"
          data-event-at={event.at}
          className="relative pl-4"
        >
          <span
            aria-hidden="true"
            className={`absolute -left-[7px] top-1 h-3 w-3 rounded-full ring-4 ring-white dark:ring-gray-900 ${
              DOT_TONE_CLASS[event.tone ?? 'default']
            }`}
          />
          <div className="text-[11px] text-muted-foreground">{formatEventTime(event.at)}</div>
          <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            {event.title}
          </div>
          {event.description && (
            <div className="text-xs text-muted-foreground">{event.description}</div>
          )}
        </li>
      ))}
    </ol>
  );
}
